import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { getAuthInstance, db } from "./firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";

import Home from "./Home";
import Login from "./Login";
import Signup from "./Signup";
import Feed from "./Feed";
import Profile from "./Profile";
import WishlistPage from "./WishListPage";
import UserPage from "./UserPage";
import WelcomeScreen from "./WelcomeScreen";
import SharedWishlistPage from "./SharedWishlistPage";
import Archived from "./Archived";
import { checkAndFixWishlists } from "./CheckFixWishlists";
import NotificationsPage from "./NotificationsPage";
import ReportsDashboard from "./ReportsDashboard";
import LikedWishlists from "./LikedWishlists";
import BottomNavBar from "./BottomNavBar";
import PrivacyPolicy from "./PrivacyPolicy";
import Terms from "./Terms";
import PremiumSuccess from "./PremiumSuccess";
import PremiumCancel from "./PremiumCancel";
import MigrationPage from "./pages/MigrationPage";
import CacheImagesMigration from "./pages/CacheImagesMigration";
import Support from "./Support";
import "./i18n";
import "./WelcomeScreen.css";
import { useTranslation } from "react-i18next";
import { getHashedIP } from "./utils";
import { useDeepLinks } from "./hooks/useDeepLinks";
import { initPushNotifications } from "./pushSetup";
import { isNative } from "./utils/isNative";
import { Capacitor } from "@capacitor/core";

function AdminFixTool() {
  const { t } = useTranslation();

  return (
    <div style={{ padding: "20px", fontFamily: "Poppins, sans-serif" }}>
      <h2>Admin Fix Tool</h2>
      <button
        onClick={async () => {
          const result = await checkAndFixWishlists();
          alert(
            result.success
              ? t(result.message || "auth.warnings.fixCompleted")
              : t(result.error),
          );
        }}
        style={{
          padding: "10px 20px",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      >
        Fix Wishlists
      </button>
    </div>
  );
}

function AppRoutes({ user }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Gestisce i deep link su mobile (quando l'app viene aperta da un link esterno)
  useDeepLinks();

  // NON mostrare WelcomeScreen per link condivisi, privacy, terms, ecc.
  const skipWelcomeRoutes = [
    "/shared",
    "/privacy",
    "/terms",
    "/premium-success",
    "/premium-cancel",
    "/support",
  ];
  const shouldSkipWelcome = skipWelcomeRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  const [showWelcome, setShowWelcome] = useState(!shouldSkipWelcome);

  // Pages where BottomNavBar should not appear
  const hideBottomNavRoutes = ["/shared", "/welcome", "/login", "/signup", "/support"];
  const shouldHideBottomNav = hideBottomNavRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  // Show WelcomeScreen for 3 seconds solo se non è un link condiviso
  useEffect(() => {
    if (!shouldSkipWelcome) {
      const timer = setTimeout(() => {
        setShowWelcome(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [shouldSkipWelcome]);

  // Show WelcomeScreen on app startup (only if not a shared link)
  if (showWelcome && !shouldSkipWelcome) {
    return <WelcomeScreen onFinish={() => setShowWelcome(false)} />;
  }

  return (
    <>
      <Routes>
        <Route path="/shared/:id" element={<SharedWishlistPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/premium-success" element={<PremiumSuccess />} />
        <Route path="/premium-cancel" element={<PremiumCancel />} />
        {!user ? (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </>
        ) : (
          <>
            <Route path="/" element={<Home />} />
            <Route path="/feed" element={<Feed />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/wishlist/:id" element={<WishlistPage />} />
            <Route path="/utente/:userId" element={<UserPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/liked" element={<LikedWishlists />} />
            <Route path="/archivio" element={<Archived />} />
            <Route path="/admin" element={<ReportsDashboard />} />
            <Route path="/admin-fix" element={<AdminFixTool />} />
            <Route path="/migrate" element={<MigrationPage />} />
            <Route path="/cache-images" element={<CacheImagesMigration />} />
            <Route path="*" element={<Navigate to="/" />} />
          </>
        )}
      </Routes>
      {user && !shouldHideBottomNav && <BottomNavBar />}
    </>
  );
}

export default function App() {
  console.log("🚀🚀🚀 WISHARE v2.1 LOADED - URL NORMALIZATION ACTIVE 🚀🚀🚀");
  const { i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // 🔔 LISTENER GLOBALE PUSH NOTIFICATIONS (solo su native)
  useEffect(() => {
    if (!isNative()) return;

    let pushListenerCleanup = null;

    const setupGlobalPushListener = async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        
        console.log("[Push][GLOBAL] Setting up global push notification listener");

        // 🎯 Listener per notifiche ricevute in foreground
        pushListenerCleanup = PushNotifications.addListener(
          'pushNotificationReceived',
          async (notification) => {
            const platform = Capacitor.getPlatform();
            console.log(
              `[Push][GLOBAL] Notifica ricevuta in foreground da piattaforma: ${platform}`,
              JSON.stringify(notification)
            );

            // Non fare nulla se non c'è un utente loggato
            if (!user?.uid) {
              console.warn("[Push][GLOBAL] Notifica ricevuta ma nessun utente loggato, skip");
              return;
            }

            try {
              // 🔥 Aggiorna Firestore per triggerare il listener della Navbar
              // La Navbar ha già un onSnapshot che conta le notifiche isRead:false
              // e aggiorna il badge iOS automaticamente
              
              // Non serve fare nulla qui! Il documento notifications è già stato creato
              // dalla Cloud Function quando la notifica è stata inviata.
              // La Navbar rileverà automaticamente il nuovo documento isRead:false
              // e aggiornerà il badge.
              
              console.log(
                "[Push][GLOBAL] Notifica ricevuta, la Navbar aggiornerà il badge automaticamente via Firestore listener"
              );
              
            } catch (err) {
              console.error("[Push][GLOBAL] Errore gestendo notifica:", err);
            }
          }
        );

        console.log("[Push][GLOBAL] ✅ Listener globale configurato con successo");
      } catch (err) {
        console.error("[Push][GLOBAL] ❌ Errore setup listener globale:", err);
      }
    };

    setupGlobalPushListener();

    return () => {
      if (pushListenerCleanup) {
        console.log("[Push][GLOBAL] Removing global push listener");
        pushListenerCleanup.remove();
      }
    };
  }, [user]);

  useEffect(() => {
    let isInitializing = true;
    let unsubscribe;

    // Setup auth state listener with dynamic import
    const setupAuth = async () => {
      try {
        // Import auth state listener from authHelpers to handle both web and native
        const { setupAuthStateListener } = await import("./utils/authHelpers");

        unsubscribe = await setupAuthStateListener(async (currentUser) => {
          if (isInitializing) {
            isInitializing = false;
          }

          setUser(currentUser);
          setLoading(false);

          if (currentUser) {
            try {
              const userRef = doc(db, "users", currentUser.uid);
              const userDoc = await getDoc(userRef);

              if (!userDoc.exists()) {
                // Create new user document
                await setDoc(
                  userRef,
                  {
                    uid: currentUser.uid,
                    displayName: currentUser.displayName || "Anonimo",
                    avatar: "",
                    isPremium: false,
                    isNewUser: true,
                    language: "it",
                    createdAt: new Date(),
                    updatedAt: new Date(),
                  },
                  { merge: true },
                );
                localStorage.removeItem("hasSeenOnboarding");
                console.log("✅ Nuovo utente creato in Firestore");
              } else {
                // Update existing user
                const userData = userDoc.data();
                const userLanguage = userData.language || "it";
                console.log("✅ Impostazione lingua:", userLanguage);
                await i18n.changeLanguage(userLanguage);

                await setDoc(
                  userRef,
                  {
                    uid: currentUser.uid,
                    displayName: currentUser.displayName || "Anonimo",
                    language: userLanguage,
                    updatedAt: new Date(),
                  },
                  { merge: true },
                );
              }

              // 🔔 Inizializza le notifiche push per questo utente (solo su native)
              if (isNative()) {
                console.log(
                  "[Push] Inizializzo le notifiche per user:",
                  currentUser.uid,
                );
                initPushNotifications(currentUser.uid);
              }
            } catch (error) {
              console.error(
                "❌ Errore durante l'inizializzazione utente:",
                error,
              );
            }
          } else {
            console.log("🔄 Utente disconnesso");
          }
        });
      } catch (error) {
        console.error("❌ Errore durante l'inizializzazione auth:", error);
        setLoading(false);
      }
    };

    setupAuth();

    // Return cleanup function
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [i18n]);

  // ⚠️ RIMOSSO: Badge reset generico all'apertura app
  // Il badge ora è gestito automaticamente dal listener Firestore in Navbar.jsx
  // che sincronizza sempre il badge con il numero reale di notifiche non lette

  if (loading) {
    return null; // Let WelcomeScreen handle the loading display
  }

  // Temporary: Add reset button for testing (remove this later)
  if (window.location.search.includes("reset=true")) {
    localStorage.removeItem("hasSeenOnboarding");
    window.location.href = "/";
    return null;
  }

  return (
    <Router>
      <AppRoutes user={user} />
    </Router>
  );
}

const styles = {
  loading: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "Poppins, sans-serif",
    fontSize: "16px",
  },
};
